import { Controller } from "@hotwired/stimulus"

// Single-date calendar popover backed by a hidden ISO (YYYY-MM-DD) input.
// Rendered by shared/components/_calendar_picker. The popover is position:fixed and placed from
// the trigger's viewport rect (like shared/_flyout) so it escapes overflow:auto containers such as
// the drawer body; it opens upward by default and flips when there isn't room. Today is bolded,
// past days are disabled. An optional deadline (e.g. a pre-payment claim's export day) is flagged and days after it are disabled.
// Weekends can be disabled too for schedules that only run on business days.
export default class extends Controller {
  static targets = ["input", "trigger", "label", "hint", "popover", "grid", "monthLabel", "quickPick"]
  static values = {
    today: String, // ISO date the server considers today (falls back to the browser's local date)
    min: String, // ISO date; days before this are disabled
    deadline: String, // ISO date; flagged in the grid, later days are disabled
    deadlineLabel: { type: String, default: "Deadline" },
    counts: { type: Object, default: {} }, // { "YYYY-MM-DD": n } shown under each day
    countsLabel: { type: String, default: "scheduled" },
    opensUp: { type: Boolean, default: true },
    skipWeekends: { type: Boolean, default: false }, // Saturdays and Sundays are disabled
  }

  // A day is "busy" when its count is clearly above the owner's typical loaded day.
  static BUSY_MULTIPLIER = 1.5
  static BUSY_FLOOR = 3
  static POPOVER_GAP = 8 // px between trigger and popover

  static WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]
  static MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]

  connect() {
    this.today = this.constructor.parseIso(this.todayValue) || this.constructor.startOfDay(new Date())
    this.selected = this.constructor.parseIso(this.inputTarget.value) || this.today
    this.visibleMonth = new Date(this.selected.getFullYear(), this.selected.getMonth(), 1)
    this.boundOutsideClick = this.outsideClick.bind(this)
    this.boundKeydown = this.keydown.bind(this)
    this.boundReposition = this.position.bind(this)
    this.busyThreshold = this.computeBusyThreshold()
    this.renderLabel()
  }

  computeBusyThreshold() {
    const values = Object.values(this.countsValue).filter((n) => n > 0)
    if (values.length === 0) return Infinity

    const avg = values.reduce((a, b) => a + b, 0) / values.length
    return Math.max(this.constructor.BUSY_FLOOR, avg * this.constructor.BUSY_MULTIPLIER)
  }

  disconnect() {
    this.close()
  }

  toggle(event) {
    event.preventDefault()
    this.popoverTarget.classList.contains("hidden") ? this.open() : this.close()
  }

  open() {
    this.visibleMonth = new Date(this.selected.getFullYear(), this.selected.getMonth(), 1)
    this.renderGrid()
    this.popoverTarget.classList.remove("hidden")
    this.position()
    this.triggerTarget.setAttribute("aria-expanded", "true")
    document.addEventListener("click", this.boundOutsideClick)
    document.addEventListener("keydown", this.boundKeydown)
    window.addEventListener("scroll", this.boundReposition, true)
    window.addEventListener("resize", this.boundReposition)
  }

  // Anchor the fixed popover to the trigger: above it by default (opensUp), below otherwise,
  // flipping when the preferred side doesn't fit in the viewport; kept inside the viewport horizontally.
  position() {
    const popover = this.popoverTarget
    const trigger = this.triggerTarget.getBoundingClientRect()
    const gap = this.constructor.POPOVER_GAP
    const height = popover.offsetHeight
    const width = popover.offsetWidth
    const fitsAbove = trigger.top - gap - height >= 0
    const fitsBelow = trigger.bottom + gap + height <= window.innerHeight
    const above = this.opensUpValue ? fitsAbove || !fitsBelow : !fitsBelow && fitsAbove

    const top = above ? trigger.top - gap - height : trigger.bottom + gap
    const left = Math.max(gap, Math.min(trigger.left, window.innerWidth - width - gap))
    popover.style.top = `${Math.max(gap, top)}px`
    popover.style.left = `${left}px`
    popover.dataset.placement = above ? "above" : "below"
  }

  close() {
    this.popoverTarget.classList.add("hidden")
    this.triggerTarget.setAttribute("aria-expanded", "false")
    document.removeEventListener("click", this.boundOutsideClick)
    document.removeEventListener("keydown", this.boundKeydown)
    window.removeEventListener("scroll", this.boundReposition, true)
    window.removeEventListener("resize", this.boundReposition)
  }

  previousMonth(event) {
    event.preventDefault()
    this.visibleMonth = new Date(this.visibleMonth.getFullYear(), this.visibleMonth.getMonth() - 1, 1)
    this.renderGrid()
    this.position()
  }

  nextMonth(event) {
    event.preventDefault()
    this.visibleMonth = new Date(this.visibleMonth.getFullYear(), this.visibleMonth.getMonth() + 1, 1)
    this.renderGrid()
    this.position()
  }

  pick(event) {
    event.preventDefault()
    const date = this.constructor.parseIso(event.currentTarget.dataset.date)
    if (!date || this.isDisabled(date)) return

    this.select(date)
    this.close()
  }

  // Quick-pick chips carry data-days="N" (relative to today).
  pickOffset(event) {
    event.preventDefault()
    const days = parseInt(event.currentTarget.dataset.days, 10)
    const date = this.constructor.addDays(this.today, days)
    if (this.isDisabled(date)) return

    this.select(date)
    this.close()
  }

  select(date) {
    this.selected = date
    this.inputTarget.value = this.constructor.toIso(date)
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.renderLabel()
  }

  // --- rendering ---

  renderLabel() {
    const date = this.selected
    const weekday = date.toLocaleDateString(undefined, { weekday: "short" })
    const monthDay = date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
    this.labelTarget.textContent = `${weekday}, ${monthDay}`

    if (this.hasHintTarget) {
      const count = this.countsValue[this.constructor.toIso(date)] || 0
      this.hintTarget.textContent = count > 0 ? `${this.relativeLabel(date)} · ${count} ${this.countsLabelValue}` : this.relativeLabel(date)
    }
    this.quickPickTargets.forEach((chip) => {
      const chipDate = this.constructor.addDays(this.today, parseInt(chip.dataset.days, 10))
      chip.dataset.selected = this.constructor.sameDay(chipDate, date) ? "true" : "false"
      chip.disabled = this.isDisabled(chipDate)
    })
  }

  relativeLabel(date) {
    const days = Math.round((date - this.today) / 86400000)
    if (days === 0) return "today"
    if (days === 1) return "tomorrow"
    if (days < 0) return `${-days} ${-days === 1 ? "day" : "days"} ago`
    return `in ${days} days`
  }

  renderGrid() {
    const year = this.visibleMonth.getFullYear()
    const month = this.visibleMonth.getMonth()
    this.monthLabelTarget.textContent = `${this.constructor.MONTHS[month]} ${year}`

    const first = new Date(year, month, 1)
    const start = this.constructor.addDays(first, -first.getDay())
    const deadline = this.constructor.parseIso(this.deadlineValue)
    const cells = []

    this.constructor.WEEKDAYS.forEach((day) => {
      cells.push(`<div class="calendar-picker-weekday">${day}</div>`)
    })

    for (let i = 0; i < 42; i++) {
      const date = this.constructor.addDays(start, i)
      const inMonth = date.getMonth() === month
      const isToday = this.constructor.sameDay(date, this.today)
      const isSelected = this.constructor.sameDay(date, this.selected)
      const disabled = this.isDisabled(date)
      const isDeadline = deadline && this.constructor.sameDay(date, deadline)
      const afterDeadline = disabled && deadline && date > deadline
      const isWeekend = this.skipWeekendsValue && this.constructor.isWeekend(date)
      const iso = this.constructor.toIso(date)
      const count = disabled ? 0 : this.countsValue[iso] || 0
      const isBusy = count >= this.busyThreshold
      const classes = ["calendar-picker-day"]
      if (!inMonth) classes.push("is-outside")
      if (isToday) classes.push("is-today")
      if (isSelected) classes.push("is-selected")
      if (disabled) classes.push("is-disabled")
      if (isDeadline) classes.push("is-deadline")
      if (afterDeadline) classes.push("is-after-deadline")
      if (isWeekend) classes.push("is-weekend")
      if (count > 0) classes.push("has-count")
      if (isBusy) classes.push("is-busy")

      const deadlineLabel = this.constructor.escapeHtml(this.deadlineLabelValue)
      const countsLabel = this.constructor.escapeHtml(this.countsLabelValue)
      let label = date.toDateString()
      if (isDeadline) label += ` — ${deadlineLabel}`
      if (afterDeadline) label += ` — after ${deadlineLabel.toLowerCase()}`
      if (isWeekend) label += " — weekend"
      if (count > 0) label += ` — ${count} ${countsLabel}${isBusy ? " (busy)" : ""}`

      const titleParts = []
      if (isDeadline) titleParts.push(deadlineLabel)
      if (count > 0) titleParts.push(`${count} ${countsLabel}`)

      cells.push(
        `<button type="button" class="${classes.join(" ")}" data-date="${iso}"` +
          ` data-action="click->calendar-picker#pick" ${disabled ? "disabled" : ""}` +
          ` aria-label="${label}" title="${titleParts.join(" · ")}"` +
          ` ${isSelected ? 'aria-pressed="true"' : ""}>` +
          `<span>${date.getDate()}</span>` +
          (count > 0 ? `<span class="calendar-picker-count">${count}</span>` : "") +
          `</button>`
      )
    }

    this.gridTarget.innerHTML = cells.join("")
  }

  // Days before min (and weekends, when skipped) are never selectable; days after the deadline
  // aren't either, unless the deadline is already before min (then there'd be nothing left to pick).
  isDisabled(date) {
    const min = this.constructor.parseIso(this.minValue)
    if (min && date < min) return true
    if (this.skipWeekendsValue && this.constructor.isWeekend(date)) return true

    const deadline = this.constructor.parseIso(this.deadlineValue)
    if (!deadline || (min && deadline < min)) return false

    return date > deadline
  }

  // --- events ---

  outsideClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  keydown(event) {
    if (event.key === "Escape") {
      this.close()
      this.triggerTarget.focus()
    }
  }

  static escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`)
  }

  // --- date helpers (local time, date-only) ---

  static startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
  }

  static addDays(date, days) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)
  }

  static isWeekend(date) {
    return date.getDay() === 0 || date.getDay() === 6
  }

  static sameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  }

  static toIso(date) {
    const pad = (n) => String(n).padStart(2, "0")
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  }

  static parseIso(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "")
    if (!match) return null
    return new Date(parseInt(match[1], 10), parseInt(match[2], 10) - 1, parseInt(match[3], 10))
  }
}
